import React, { useState, useRef, useEffect } from "react";
import { decodeAndSanitizeHtml } from "./textAnalysisUtils";
import TextConversationMobile from "./TextConversationMobile";
import ReadOnlyBanner from "../../common/ReadOnlyBanner";
import "./css/TextMessageAnalysis.css";
import "./css/TextConversationAnalysis.css";
import "../../css/Common.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faQuestionCircle,
  faShieldAlt,
  faTimes,
  faFileText,
  faPaperPlane,
  faChartBar,
  faEdit,
  faPlus,
  faComments,
  faTrash,
  faUser,
  faUserShield,
  faCalendar,
  faClock,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import InternationalPhoneInput from "../../utils/InternationalPhoneInputUtil";
import { useTheme } from "../../context/ThemeContext";
import { useLoading } from "../../../../contexts/LoadingContext";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { formatDateTime } from "../../utils/DateTimeUtil";
import DisclosuresModal from "../common/DisclosuresModal";
import ScamDetectorAPI from "../../services/ScamDetectorAPI";
import ScamReportAPI from "../../services/ScamReportAPI";
import HelpGuideButton from "../../HelpGuideButton";
import EmptyStatePanel from "../common/EmptyStatePanel";

function TextConversationAnalysis() {
  // Mobile detection with proper state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Theme hook
  const { effectiveTheme } = useTheme();

  // Loading hook for full-screen modal
  const { isLoading, showLoading, hideLoading } = useLoading();

  const { t } = useTranslation();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [messages, setMessages] = useState([
    { id: 1, sender: "them", text: "", timestamp: "" },
  ]);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [error, setError] = useState(null);
  const [hasUserInput, setHasUserInput] = useState(false);
  const [activeTab, setActiveTab] = useState("input"); // For mobile tabs: input, results
  const [phoneValidation, setPhoneValidation] = useState({
    isValid: true,
    isComplete: false,
  });
  const [phoneInputKey, setPhoneInputKey] = useState(0);

  // Message ID counter
  const [nextMessageId, setNextMessageId] = useState(2);

  // Scroll indicators
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const messagesListRef = useRef(null);

  // Handle scroll to update indicators
  const handleMessagesScroll = (e) => {
    const element = e.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    setShowScrollTop(scrollTop > 10);
    setShowScrollBottom(scrollTop + clientHeight < scrollHeight - 10);
  };

  // Scroll to top/bottom when clicking indicators
  const scrollToTop = () => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scrollToBottom = () => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTo({
        top: messagesListRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // Check scroll on messages update
  useEffect(() => {
    if (messagesListRef.current) {
      const element = messagesListRef.current;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      const hasOverflow = scrollHeight > clientHeight;
      console.log("Scroll check:", {
        scrollHeight,
        clientHeight,
        hasOverflow,
        messagesCount: messages.length,
      });
      setShowScrollBottom(hasOverflow);
    }
  }, [messages]);

  // Reusable validation functions
  const shouldShowPhoneValidationError = () => {
    return (
      !isPhoneDisabled &&
      phoneNumber &&
      phoneNumber.length > 2 &&
      phoneValidation.isComplete &&
      !phoneValidation.isValid
    );
  };

  const handlePhoneChange = (e164Value, validationInfo) => {
    setPhoneNumber(e164Value);

    if (validationInfo) {
      setPhoneValidation({
        isValid: validationInfo.isValid,
        isComplete: validationInfo.isComplete,
      });
    }

    updateHasUserInput(e164Value, messages);
  };

  const updateHasUserInput = (phone, msgs) => {
    const hasPhone = phone && phone.length > 2;
    const hasMessages = msgs.some((msg) => msg.text.trim().length > 0);

    setHasUserInput(hasPhone || hasMessages);
  };

  const handleMessageTextChange = (messageId, value) => {
    const updatedMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, text: value } : msg,
    );
    setMessages(updatedMessages);
    updateHasUserInput(phoneNumber, updatedMessages);
  };

  const handleMessageSenderChange = (messageId, sender) => {
    const updatedMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, sender } : msg,
    );
    setMessages(updatedMessages);
  };

  const handleMessageTimestampChange = (messageId, timestamp) => {
    const updatedMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, timestamp } : msg,
    );
    setMessages(updatedMessages);
  };

  const addMessage = () => {
    const lastMessage = messages[messages.length - 1];
    const newSender = lastMessage.sender === "them" ? "you" : "them";

    setMessages([
      ...messages,
      { id: nextMessageId, sender: newSender, text: "", timestamp: "" },
    ]);
    setNextMessageId(nextMessageId + 1);
  };

  const removeMessage = (messageId) => {
    if (messages.length <= 1) return; // Keep at least one message
    const updatedMessages = messages.filter((msg) => msg.id !== messageId);
    setMessages(updatedMessages);
    updateHasUserInput(phoneNumber, updatedMessages);
  };

  const handleClearAnalysis = () => {
    setPhoneNumber("");
    setMessages([{ id: 1, sender: "them", text: "", timestamp: "" }]);
    setNextMessageId(2);
    setHasUserInput(false);
    setAnalysisResults(null);
    setError(null);
    setPhoneInputKey((k) => k + 1);
  };

  // Check if phone number field should be disabled
  const isPhoneDisabled = isLoading;

  // Check if form is valid for submission
  const isFormValid = () => {
    // Must have at least one message with text
    const hasValidMessages = messages.some((msg) => msg.text.trim().length > 0);
    if (!hasValidMessages) return false;

    // Must have valid phone number
    const hasValidPhone =
      phoneNumber && phoneNumber.length > 2 && phoneValidation.isValid;

    return hasValidPhone;
  };

  // Render tab navigation for mobile
  const renderTabNavigation = () => {
    const tabs = [
      { id: "input", label: t("textConv.tab.input"), icon: faEdit },
      {
        id: "results",
        label: t("textConv.tab.results"),
        icon: faChartBar,
        disabled: !analysisResults,
      },
    ];

    return (
      <div className="tab-navigation">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? "active" : ""} ${
              tab.disabled ? "disabled" : ""
            }`}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
          >
            <FontAwesomeIcon icon={tab.icon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber.trim()) {
      setError(t("textConv.error.missingPhone"));
      return;
    }

    const validMessages = messages.filter((msg) => msg.text.trim().length > 0);
    if (validMessages.length === 0) {
      setError(t("textConv.error.noMessages"));
      return;
    }

    showLoading(t("textConv.loading.title"), t("textConv.loading.subtitle"));
    setError(null);

    try {
      const senderIdentifier = phoneNumber.trim() || null;

      // Format the conversation history for analysis
      const conversationText = validMessages
        .map((msg, idx) => {
          const senderLabel = msg.sender === "you" ? "You" : "Them";
          const timestamp = msg.timestamp ? ` [${msg.timestamp}]` : "";
          return `Message ${
            idx + 1
          }${timestamp} - ${senderLabel}: ${msg.text.trim()}`;
        })
        .join("\n\n");

      // Add context about analyzing conversation history
      const analysisContext = `CONVERSATION HISTORY ANALYSIS - Analyzing ${validMessages.length} message(s) for patterns of trust-building scams (pig butchering, romance scams, etc.). Pay special attention to relationship progression, topic shifts, and long-term manipulation tactics. IMPORTANT: Messages that appear to be innocent wrong numbers, harmless greetings, unsolicited offers, friendly conversation starters, or any type of unsolicited contact from an unknown sender could be the opening phase of a pig butchering scam. In these schemes, scammers deliberately send innocent-seeming messages to initiate contact — the fraud only becomes apparent over time as trust is built and the topic shifts toward investments or cryptocurrency. Flag any unsolicited contact patterns and educate the user about this tactic.\n\n${conversationText}`;

      // Perform analysis using the unified endpoint
      const combinedResults = await ScamDetectorAPI.analyzeText(
        senderIdentifier,
        analysisContext,
      );

      console.log(
        "[TextConversationAnalysis] Full API Response:",
        JSON.stringify(combinedResults, null, 2),
      );
      setAnalysisResults(combinedResults);

      // Save report on detection
      const riskLevel = (
        combinedResults.textAnalysis?.riskLevel ||
        combinedResults.phoneAnalysis?.riskLevel ||
        ""
      ).toLowerCase();
      if (
        riskLevel === "high" ||
        riskLevel === "danger" ||
        riskLevel === "medium" ||
        riskLevel === "warning"
      ) {
        try {
          const saveResponse = await ScamReportAPI.saveReport({
            analysisType: "TEXT_ANALYSIS",
            analysisTitle: `${riskLevel === "high" || riskLevel === "danger" ? "High" : "Medium"} Risk — Conversation`,
            analysisDetail:
              combinedResults.textAnalysis?.explanation ||
              "Suspicious conversation content detected",
            analysisInput: analysisContext,
            isScam: true,
            confidence:
              combinedResults.textAnalysis?.confidence ||
              combinedResults.phoneAnalysis?.confidence ||
              0,
            riskLevel: riskLevel,
            explanation: combinedResults.textAnalysis?.explanation || "",
            suggestions: combinedResults.textAnalysis?.suggestions || "",
          });
          if (saveResponse.success) {
            setReportId(saveResponse.reportId);
          }
        } catch (saveErr) {
          console.error(
            "[TextConversationAnalysis] Failed to save report:",
            saveErr,
          );
        }
      }

      // Auto-switch to results tab on mobile after successful analysis
      if (isMobile) {
        setActiveTab("results");
      }
    } catch (err) {
      console.error("[TextConversationAnalysis] Analysis failed:", err);

      if (err.code === "TIMEOUT") {
        setError(t("textConv.error.timeout"));
      } else if (err.response) {
        const status = err.response.status;
        const statusText = err.response.statusText || "Unknown error";
        setError(
          t("textConv.error.server")
            .replace("{status}", status)
            .replace("{text}", statusText),
        );
      } else if (err.message && err.message.includes("Failed to fetch")) {
        setError(t("textConv.error.network"));
      } else {
        setError(t("textConv.error.generic"));
      }
    } finally {
      hideLoading();
    }
  };

  const getRiskLevelIcon = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "high":
      case "danger":
        return faTimesCircle;
      case "medium":
      case "warning":
        return faExclamationTriangle;
      case "low":
      case "safe":
        return faCheckCircle;
      default:
        return faQuestionCircle;
    }
  };

  const getRiskLevelClass = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case "high":
      case "danger":
        return "risk-high";
      case "medium":
      case "warning":
        return "risk-medium";
      case "low":
      case "safe":
        return "risk-low";
      default:
        return "risk-unknown";
    }
  };

  const getOverallRiskLevel = () => {
    if (!analysisResults) return null;

    const phoneRisk = analysisResults.phoneAnalysis?.riskLevel?.toLowerCase();
    const textRisk = analysisResults.textAnalysis?.riskLevel?.toLowerCase();

    const riskPriority = {
      high: 4,
      danger: 4,
      medium: 3,
      warning: 3,
      low: 2,
      safe: 2,
      unknown: 1,
    };

    const phoneLevel = riskPriority[phoneRisk] || 0;
    const textLevel = riskPriority[textRisk] || 0;

    if (phoneLevel >= textLevel && phoneRisk) {
      return analysisResults.phoneAnalysis.riskLevel;
    } else if (textLevel > phoneLevel && textRisk) {
      return analysisResults.textAnalysis.riskLevel;
    }

    return phoneRisk
      ? analysisResults.phoneAnalysis.riskLevel
      : textRisk
        ? analysisResults.textAnalysis.riskLevel
        : "Unknown";
  };

  const getOverallRiskType = () => {
    if (!analysisResults) return null;

    if (analysisResults.textAnalysis?.category) {
      return analysisResults.textAnalysis.category;
    }

    if (analysisResults.phoneAnalysis?.category) {
      return analysisResults.phoneAnalysis.category;
    }

    if (
      analysisResults.textAnalysis?.riskFactors &&
      analysisResults.textAnalysis.riskFactors.length > 0
    ) {
      return analysisResults.textAnalysis.riskFactors[0];
    }

    return null;
  };

  const getOverallRecommendation = () => {
    if (!analysisResults) return null;

    if (analysisResults.textAnalysis?.suggestions) {
      return analysisResults.textAnalysis.suggestions;
    }

    if (analysisResults.phoneAnalysis?.recommendation) {
      return analysisResults.phoneAnalysis.recommendation;
    }

    const riskLevel = getOverallRiskLevel()?.toLowerCase();
    switch (riskLevel) {
      case "high":
      case "danger":
        return t("textConv.risk.highRecommendation");
      case "medium":
      case "warning":
        return t("textConv.risk.mediumRecommendation");
      case "low":
      case "safe":
        return t("textConv.risk.lowRecommendation");
      default:
        return t("textConv.risk.unknownRecommendation");
    }
  };

  // Render message input fields
  const renderMessageInput = (message, index) => {
    const isFromThem = message.sender === "them";
    return (
      <div
        key={message.id}
        className={`imessage-bubble-wrapper ${
          isFromThem ? "wrapper-left" : "wrapper-right"
        }`}
      >
        <div className="sender-btn-group">
          <button
            type="button"
            className={`sender-btn-bubble them-btn ${
              isFromThem ? "active" : ""
            }`}
            onClick={() => handleMessageSenderChange(message.id, "them")}
            disabled={isLoading}
            title={t("textConv.form.fromThem")}
          >
            <FontAwesomeIcon icon={faUser} />
          </button>
          <span className="sender-label">{t("textConv.form.them")}</span>
        </div>
        <div
          className={`imessage-bubble-container ${
            isFromThem ? "bubble-left" : "bubble-right"
          }`}
        >
          <div className="bubble-controls">
            <span className="message-number-bubble">#{index + 1}</span>
            {messages.length > 1 && (
              <button
                type="button"
                onClick={() => removeMessage(message.id)}
                disabled={isLoading}
                className="remove-bubble-btn"
                title={t("textConv.form.remove")}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
          <div className={`imessage-bubble ${isFromThem ? "them" : "you"}`}>
            <textarea
              value={message.text}
              onChange={(e) =>
                handleMessageTextChange(message.id, e.target.value)
              }
              placeholder={
                isFromThem
                  ? t("textConv.form.theirMessage")
                  : t("textConv.form.yourMessage")
              }
              rows={2}
              disabled={isLoading}
              className="bubble-text-input"
              maxLength={1000}
            />
          </div>
        </div>
        <div className="sender-btn-group">
          <button
            type="button"
            className={`sender-btn-bubble you-btn ${
              !isFromThem ? "active" : ""
            }`}
            onClick={() => handleMessageSenderChange(message.id, "you")}
            disabled={isLoading}
            title={t("textConv.form.fromYou")}
          >
            <FontAwesomeIcon icon={faUserShield} />
          </button>
          <span className="sender-label">{t("textConv.form.you")}</span>
        </div>
      </div>
    );
  };

  // Render input tab content for mobile
  const renderInputTab = () => (
    <div className="tab-content input-tab">
      <div className="tab-header">
        <h3>
          <FontAwesomeIcon icon={faComments} />
          {t("textConv.form.sectionTitle")}
        </h3>
        <p className="tab-subtitle">{t("textConv.form.subtitle")}</p>
      </div>

      {error && (
        <div className="error-message">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="analysis-form">
        {/* Sender Information Row */}
        <div className="sender-info-compact">
          <label className="section-label">
            <FontAwesomeIcon icon={faPhone} />
            {t("textConv.form.senderInfo")}
          </label>
          <div className="sender-inputs-inline">
            <div className="phone-input-wrapper">
              <InternationalPhoneInput
                key={phoneInputKey}
                id="phone-number"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder={t("textAnalysis.form.phonePlaceholder")}
                disabled={isPhoneDisabled}
                className="phone-input-inline"
              />
              {shouldShowPhoneValidationError() && (
                <div className="validation-error-inline">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {t("textConv.form.invalid")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Message History - Scrollable */}
        <div className="messages-section-compact scrollable-section">
          <label className="section-label">
            <FontAwesomeIcon icon={faComments} />
            {t("textConv.form.messagesLabel")}
          </label>

          <div className="messages-list-wrapper">
            {showScrollTop && (
              <div
                className="scroll-indicator scroll-indicator-top"
                onClick={scrollToTop}
              >
                <FontAwesomeIcon icon={faChevronUp} />
              </div>
            )}
            <div
              className="messages-list-compact"
              ref={messagesListRef}
              onScroll={handleMessagesScroll}
            >
              {messages.map((message, index) =>
                renderMessageInput(message, index),
              )}
            </div>
            {showScrollBottom && (
              <div
                className="scroll-indicator scroll-indicator-bottom"
                onClick={scrollToBottom}
              >
                <FontAwesomeIcon icon={faChevronDown} />
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={addMessage}
            disabled={isLoading}
            className="add-message-btn-compact"
          >
            <FontAwesomeIcon icon={faPlus} />
            {t("textConv.form.addMessage")}
          </button>
        </div>
      </form>

      {/* Submit Buttons - Fixed at bottom */}
      <div className="btn-group">
        <button
          type="submit"
          form="message-analysis-form"
          disabled={isLoading || !isFormValid()}
          className="btn btn-primary"
          onClick={handleSubmit}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
          {isLoading
            ? t("textAnalysis.form.analyzing")
            : t("textConv.form.analyzeBtn")}
        </button>

        {hasUserInput && (
          <button
            type="button"
            onClick={handleClearAnalysis}
            disabled={isLoading}
            className="btn btn-ghost"
          >
            <FontAwesomeIcon icon={faTimes} />
            {t("textConv.form.clearAll")}
          </button>
        )}
      </div>
    </div>
  );

  // Render results tab content for mobile
  const renderResultsTab = () => (
    <div className="tab-content results-tab">
      {analysisResults ? (
        <div className="analysis-results">
          <div className="results-header">
            <FontAwesomeIcon icon={faChartBar} className="results-icon" />
            <h4>{t("textConv.results.conversationHeading")}</h4>
          </div>

          {/* Overall Risk Assessment */}
          <div className="analysis-section overall-risk-section">
            <h4>{t("textConv.results.overallRisk")}</h4>
            <div className="compact-analysis">
              <div className="analysis-line">
                <strong>{t("textAnalysis.phone.riskLevel")}:</strong>{" "}
                <span
                  className={`risk-indicator ${getRiskLevelClass(
                    getOverallRiskLevel(),
                  )}`}
                >
                  <FontAwesomeIcon
                    icon={getRiskLevelIcon(getOverallRiskLevel())}
                  />
                  {getOverallRiskLevel()}
                </span>
              </div>
              {getOverallRiskType() && (
                <div className="analysis-line">
                  <strong>{t("textConv.results.riskType")}:</strong>{" "}
                  {getOverallRiskType()}
                </div>
              )}
              {getOverallRecommendation() && (
                <div className="analysis-line">
                  <strong>{t("textConv.results.recommendationLabel")}:</strong>{" "}
                  {getOverallRecommendation()}
                </div>
              )}
            </div>
          </div>

          {/* Detailed Breakdown Section Header */}
          <div className="analysis-section-divider">
            <h4>{t("textConv.results.detailedBreakdown")}</h4>
          </div>

          {/* Phone Number Analysis */}
          {analysisResults.phoneAnalysis && (
            <div className="analysis-section">
              <h4>{t("textAnalysis.phone.title")}</h4>
              <div className="compact-analysis">
                {analysisResults.phoneAnalysis.riskLevel && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.phone.riskLevel")}:</strong>{" "}
                    <span
                      className={`risk-indicator de-emphasized ${getRiskLevelClass(
                        analysisResults.phoneAnalysis.riskLevel,
                      )}`}
                    >
                      <FontAwesomeIcon
                        icon={getRiskLevelIcon(
                          analysisResults.phoneAnalysis.riskLevel,
                        )}
                      />
                      {analysisResults.phoneAnalysis.riskLevel}
                    </span>
                  </div>
                )}
                {analysisResults.phoneAnalysis.description && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.phone.analysis")}:</strong>{" "}
                    {analysisResults.phoneAnalysis.description}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Text Analysis Results */}
          {analysisResults.textAnalysis && (
            <div className="analysis-section">
              <h4>{t("textConv.results.conversationPattern")}</h4>
              <div className="compact-analysis">
                {analysisResults.textAnalysis.riskLevel && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.phone.riskLevel")}:</strong>{" "}
                    <span
                      className={`risk-indicator de-emphasized ${getRiskLevelClass(
                        analysisResults.textAnalysis.riskLevel,
                      )}`}
                    >
                      <FontAwesomeIcon
                        icon={getRiskLevelIcon(
                          analysisResults.textAnalysis.riskLevel,
                        )}
                      />
                      {analysisResults.textAnalysis.riskLevel}
                    </span>
                  </div>
                )}
                {analysisResults.textAnalysis.confidence && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.textSection.confidence")}:</strong>{" "}
                    {analysisResults.textAnalysis.confidence}
                  </div>
                )}
                {analysisResults.textAnalysis.category && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.textSection.category")}:</strong>{" "}
                    {analysisResults.textAnalysis.category}
                  </div>
                )}
                {analysisResults.textAnalysis.scamType && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.textSection.scamType")}:</strong>{" "}
                    {analysisResults.textAnalysis.scamType}
                  </div>
                )}
                {analysisResults.textAnalysis.description && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.textSection.analysis")}:</strong>{" "}
                    {analysisResults.textAnalysis.description}
                  </div>
                )}
                {analysisResults.textAnalysis.explanation && (
                  <div className="analysis-line top-aligned">
                    <strong>
                      {t("textAnalysis.textSection.explanation")}:
                    </strong>{" "}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: decodeAndSanitizeHtml(
                          analysisResults.textAnalysis.explanation,
                        ),
                      }}
                    />
                  </div>
                )}
                {analysisResults.textAnalysis.suggestions && (
                  <div className="analysis-line">
                    <strong>
                      {t("textConv.results.recommendationLabel")}:
                    </strong>{" "}
                    {analysisResults.textAnalysis.suggestions}
                  </div>
                )}
                {analysisResults.textAnalysis.riskFactors &&
                  analysisResults.textAnalysis.riskFactors.length > 0 && (
                    <div className="analysis-line">
                      <strong>{t("textConv.results.detectedThreats")}:</strong>{" "}
                      {analysisResults.textAnalysis.riskFactors.join(", ")}
                    </div>
                  )}
                {analysisResults.textAnalysis.indicators &&
                  Array.isArray(analysisResults.textAnalysis.indicators) &&
                  analysisResults.textAnalysis.indicators.length > 0 && (
                    <div className="analysis-line">
                      <strong>{t("textConv.results.riskIndicators")}:</strong>{" "}
                      {analysisResults.textAnalysis.indicators.join(", ")}
                    </div>
                  )}
                {analysisResults.textAnalysis.score && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.textSection.riskScore")}:</strong>{" "}
                    {analysisResults.textAnalysis.score}
                  </div>
                )}
                {analysisResults.textAnalysis.reasoning && (
                  <div className="analysis-line">
                    <strong>{t("textAnalysis.textSection.reasoning")}:</strong>{" "}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: decodeAndSanitizeHtml(
                          analysisResults.textAnalysis.reasoning,
                        ),
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Analysis Metadata */}
          {analysisResults.analysisDate && (
            <div className="analysis-metadata">
              <small>
                {t("textAnalysis.results.completedOn")}{" "}
                {formatDateTime(analysisResults.analysisDate)}
              </small>
            </div>
          )}
        </div>
      ) : (
        <div className="tab-content results-tab empty-state">
          <EmptyStatePanel
            icon={faComments}
            title={t("textConv.empty.noResultsTitle")}
            description={t("textConv.empty.noResultsDesc")}
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`common-container ${isMobile ? "tcam-mobile-active" : ""}`}
      data-theme={effectiveTheme}
    >
      <ReadOnlyBanner />
      {!isMobile && (
        <div className="common-header">
          <h1 className="main-title">{t("textConv.page.title")}</h1>
          <div className="subtitle-with-help">
            <p className="common-subtitle">{t("textConv.page.subtitle")}</p>
            <HelpGuideButton guideId={3} />
          </div>
        </div>
      )}

      {/* Mobile Layout */}
      {isMobile ? (
        <TextConversationMobile
          phoneNumber={phoneNumber}
          messages={messages}
          analysisResults={analysisResults}
          error={error}
          isLoading={isLoading}
          hasUserInput={hasUserInput}
          phoneValidation={phoneValidation}
          isPhoneDisabled={isPhoneDisabled}
          handlePhoneChange={handlePhoneChange}
          handleMessageTextChange={handleMessageTextChange}
          handleMessageSenderChange={handleMessageSenderChange}
          addMessage={addMessage}
          removeMessage={removeMessage}
          handleClearAnalysis={handleClearAnalysis}
          handleSubmit={handleSubmit}
          isFormValid={isFormValid}
          shouldShowPhoneValidationError={shouldShowPhoneValidationError}
          getOverallRiskLevel={getOverallRiskLevel}
          getOverallRiskType={getOverallRiskType}
          getOverallRecommendation={getOverallRecommendation}
          reportId={reportId}
          phoneInputKey={phoneInputKey}
        />
      ) : (
        /* Desktop Layout */
        <div className="common-layout">
          <div className="analysis-form-container" style={{ flex: 1 }}>
            <div className="common-section-header">
              <h3>
                <FontAwesomeIcon icon={faEdit} />
                {t("textConv.form.heading")}
              </h3>
            </div>
            <div className="panel-content scrollable-panel">
              {error && (
                <div className="error-message">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="analysis-form">
                {/* Sender Information Row */}
                <div className="sender-info-section">
                  <div className="sender-inputs-row">
                    {/* Phone Number Input */}
                    <div className="input-column">
                      <div
                        className={`input-header compact ${
                          isPhoneDisabled ? "disabled" : ""
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faPhone}
                          className="section-icon"
                        />
                        <label htmlFor="phone-number">
                          {t("textAnalysis.form.phoneLabel")}
                        </label>
                      </div>
                      <div className="phone-input-container">
                        <InternationalPhoneInput
                          key={phoneInputKey}
                          id="phone-number"
                          value={phoneNumber}
                          onChange={handlePhoneChange}
                          placeholder={t("textAnalysis.form.phonePlaceholder")}
                          disabled={isPhoneDisabled}
                          className="phone-input compact"
                        />
                        {shouldShowPhoneValidationError() && (
                          <div className="validation-error">
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                            {t("textAnalysis.form.invalidPhone")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Message History */}
                <div className="messages-section-compact">
                  <label className="section-label">
                    <FontAwesomeIcon icon={faComments} />
                    {t("textConv.form.messagesLabel")}
                  </label>

                  <div className="messages-list-wrapper">
                    {showScrollTop && (
                      <div
                        className="scroll-indicator scroll-indicator-top"
                        onClick={scrollToTop}
                      >
                        <FontAwesomeIcon icon={faChevronUp} />
                      </div>
                    )}
                    <div
                      className="messages-list-compact"
                      ref={messagesListRef}
                      onScroll={handleMessagesScroll}
                    >
                      {messages.map((message, index) =>
                        renderMessageInput(message, index),
                      )}
                    </div>
                    {showScrollBottom && (
                      <div
                        className="scroll-indicator scroll-indicator-bottom"
                        onClick={scrollToBottom}
                      >
                        <FontAwesomeIcon icon={faChevronDown} />
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={addMessage}
                    disabled={isLoading}
                    className="add-message-btn-compact"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    {t("textConv.form.addMessage")}
                  </button>
                </div>
              </form>
            </div>

            {/* Submit Buttons - Fixed outside scrollable area */}
            <div className="btn-group">
              <button
                type="submit"
                disabled={isLoading || !isFormValid()}
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {isLoading
                  ? t("textAnalysis.form.analyzing")
                  : t("textConv.form.analyzeBtn")}
              </button>

              {hasUserInput && (
                <button
                  type="button"
                  onClick={handleClearAnalysis}
                  disabled={isLoading}
                  className="btn btn-ghost"
                >
                  <FontAwesomeIcon icon={faTimes} />
                  {t("textConv.form.clearAll")}
                </button>
              )}
            </div>
          </div>

          {/* Results Display */}
          <div className="analysis-details" style={{ flex: 1 }}>
            <div className="common-section-header">
              <h3>
                <FontAwesomeIcon icon={faChartBar} />
                {t("textConv.results.heading")}
              </h3>
            </div>
            <div className="panel-content">
              {analysisResults ? (
                <>
                  {/* Overall Risk Assessment */}
                  <div className="analysis-section overall-risk-section">
                    <h4>{t("textConv.results.overallRisk")}</h4>
                    <div className="compact-analysis">
                      <div className="analysis-line">
                        <strong>{t("textAnalysis.phone.riskLevel")}:</strong>{" "}
                        <span
                          className={`risk-indicator ${getRiskLevelClass(
                            getOverallRiskLevel(),
                          )}`}
                        >
                          <FontAwesomeIcon
                            icon={getRiskLevelIcon(getOverallRiskLevel())}
                          />
                          {getOverallRiskLevel()}
                        </span>
                      </div>
                      {getOverallRiskType() && (
                        <div className="analysis-line">
                          <strong>{t("textConv.results.riskType")}:</strong>{" "}
                          {getOverallRiskType()}
                        </div>
                      )}
                      {getOverallRecommendation() && (
                        <div className="analysis-line">
                          <strong>
                            {t("textConv.results.recommendationLabel")}:
                          </strong>{" "}
                          {getOverallRecommendation()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Detailed Breakdown Section Header */}
                  <div className="analysis-section-divider">
                    <h4>{t("textConv.results.detailedBreakdown")}</h4>
                  </div>

                  {/* Phone Number Analysis */}
                  {analysisResults.phoneAnalysis && (
                    <div className="analysis-section">
                      <h4>{t("textAnalysis.phone.title")}</h4>
                      <div className="compact-analysis">
                        {analysisResults.phoneAnalysis.riskLevel && (
                          <div className="analysis-line">
                            <strong>
                              {t("textAnalysis.phone.riskLevel")}:
                            </strong>{" "}
                            <span
                              className={`risk-indicator de-emphasized ${getRiskLevelClass(
                                analysisResults.phoneAnalysis.riskLevel,
                              )}`}
                            >
                              <FontAwesomeIcon
                                icon={getRiskLevelIcon(
                                  analysisResults.phoneAnalysis.riskLevel,
                                )}
                              />
                              {analysisResults.phoneAnalysis.riskLevel}
                            </span>
                          </div>
                        )}
                        {analysisResults.phoneAnalysis.description && (
                          <div className="analysis-line">
                            <strong>{t("textAnalysis.phone.analysis")}:</strong>{" "}
                            {analysisResults.phoneAnalysis.description}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Text Analysis Results */}
                  {analysisResults.textAnalysis && (
                    <div className="analysis-section">
                      <h4>{t("textConv.results.conversationPattern")}</h4>
                      <div className="compact-analysis">
                        {analysisResults.textAnalysis.riskLevel && (
                          <div className="analysis-line">
                            <strong>
                              {t("textAnalysis.phone.riskLevel")}:
                            </strong>{" "}
                            <span
                              className={`risk-indicator de-emphasized ${getRiskLevelClass(
                                analysisResults.textAnalysis.riskLevel,
                              )}`}
                            >
                              <FontAwesomeIcon
                                icon={getRiskLevelIcon(
                                  analysisResults.textAnalysis.riskLevel,
                                )}
                              />
                              {analysisResults.textAnalysis.riskLevel}
                            </span>
                          </div>
                        )}
                        {analysisResults.textAnalysis.confidence && (
                          <div className="analysis-line">
                            <strong>
                              {t("textAnalysis.textSection.confidence")}:
                            </strong>{" "}
                            {analysisResults.textAnalysis.confidence}
                          </div>
                        )}
                        {analysisResults.textAnalysis.category && (
                          <div className="analysis-line">
                            <strong>
                              {t("textAnalysis.textSection.category")}:
                            </strong>{" "}
                            {analysisResults.textAnalysis.category}
                          </div>
                        )}
                        {analysisResults.textAnalysis.scamType && (
                          <div className="analysis-line">
                            <strong>
                              {t("textAnalysis.textSection.scamType")}:
                            </strong>{" "}
                            {analysisResults.textAnalysis.scamType}
                          </div>
                        )}
                        {analysisResults.textAnalysis.description && (
                          <div className="analysis-line">
                            <strong>
                              {t("textAnalysis.textSection.analysis")}:
                            </strong>{" "}
                            {analysisResults.textAnalysis.description}
                          </div>
                        )}
                        {analysisResults.textAnalysis.explanation && (
                          <div className="analysis-line top-aligned">
                            <strong>
                              {t("textAnalysis.textSection.explanation")}:
                            </strong>{" "}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: decodeAndSanitizeHtml(
                                  analysisResults.textAnalysis.explanation,
                                ),
                              }}
                            />
                          </div>
                        )}
                        {analysisResults.textAnalysis.suggestions && (
                          <div className="analysis-line">
                            <strong>
                              {t("textConv.results.recommendationLabel")}:
                            </strong>{" "}
                            {analysisResults.textAnalysis.suggestions}
                          </div>
                        )}
                        {analysisResults.textAnalysis.riskFactors &&
                          analysisResults.textAnalysis.riskFactors.length >
                            0 && (
                            <div className="analysis-line">
                              <strong>
                                {t("textConv.results.detectedThreats")}:
                              </strong>{" "}
                              {analysisResults.textAnalysis.riskFactors.join(
                                ", ",
                              )}
                            </div>
                          )}
                        {analysisResults.textAnalysis.indicators &&
                          Array.isArray(
                            analysisResults.textAnalysis.indicators,
                          ) &&
                          analysisResults.textAnalysis.indicators.length >
                            0 && (
                            <div className="analysis-line">
                              <strong>
                                {t("textConv.results.riskIndicators")}:
                              </strong>{" "}
                              {analysisResults.textAnalysis.indicators.join(
                                ", ",
                              )}
                            </div>
                          )}
                        {analysisResults.textAnalysis.score && (
                          <div className="analysis-line">
                            <strong>
                              {t("textAnalysis.textSection.riskScore")}:
                            </strong>{" "}
                            {analysisResults.textAnalysis.score}
                          </div>
                        )}
                        {analysisResults.textAnalysis.reasoning && (
                          <div className="analysis-line">
                            <strong>
                              {t("textAnalysis.textSection.reasoning")}:
                            </strong>{" "}
                            <div
                              dangerouslySetInnerHTML={{
                                __html: decodeAndSanitizeHtml(
                                  analysisResults.textAnalysis.reasoning,
                                ),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Analysis Metadata */}
                  {analysisResults.analysisDate && (
                    <div className="analysis-metadata">
                      <small>
                        {t("textAnalysis.results.completedOn")}{" "}
                        {formatDateTime(analysisResults.analysisDate)}
                      </small>
                    </div>
                  )}
                </>
              ) : (
                <EmptyStatePanel
                  icon={faComments}
                  title={t("textConv.empty.readyTitle")}
                  description={t("textConv.empty.readyDesc")}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <DisclosuresModal disclaimerText="💡 Privacy Note: Your message data is analyzed securely and not stored permanently. This tool is for educational purposes only. For complete terms and limitations, please review our" />
    </div>
  );
}

export default TextConversationAnalysis;
